import { Node, Edge, MarkerType } from '@xyflow/react';
import { DatabaseNodeData } from '../types/diagram.types';

const mkDb = (
  id: string,
  serviceId: string,
  dbType: DatabaseNodeData['dbType'],
  dbName: string,
  tables: DatabaseNodeData['tables'],
  x: number,
  y: number
): Node<DatabaseNodeData> => ({
  id,
  type: 'database',
  position: { x, y },
  data: { label: dbName, dbType, dbName, serviceId, tables, connected: false },
  draggable: true,
  selectable: true,
});

export const DATABASE_NODES: Node<DatabaseNodeData>[] = [
  // === Auth/User zone (y=1500) ===
  mkDb('db-users-pg', 'svc-003', 'PostgreSQL', 'users_db', [
    { name: 'users', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'email', type: 'varchar(255)', nullable: false },
      { name: 'phone', type: 'varchar(20)', nullable: true },
      { name: 'kind', type: 'varchar(20)', notes: 'person/company' },
      { name: 'type', type: 'varchar(20)', notes: 'client/manager' },
      { name: 'status', type: 'varchar(20)', notes: 'active/blocked' },
      { name: 'check_status', type: 'varchar(20)', notes: 'kyc status' },
      { name: 'is_enabled', type: 'boolean' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
    { name: 'user_checks', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid', fk: 'users.id' },
      { name: 'provider', type: 'varchar(50)' },
      { name: 'status', type: 'varchar(20)' },
      { name: 'score', type: 'int' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
    { name: 'user_documents', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid', fk: 'users.id' },
      { name: 'type', type: 'varchar(50)' },
      { name: 's3_key', type: 'varchar(512)' },
      { name: 'status', type: 'varchar(20)' },
    ]},
  ], 100, 1500),

  mkDb('db-accounts-pg', 'svc-004', 'PostgreSQL', 'accounts_db', [
    { name: 'accounts', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid', fk: 'users.id' },
      { name: 'name', type: 'varchar(100)' },
      { name: 'currency', type: 'char(3)' },
      { name: 'kind', type: 'varchar(20)' },
      { name: 'sum', type: 'numeric(20,8)' },
      { name: 'sum_is_blocked', type: 'numeric(20,8)' },
      { name: 'sum_is_reserved', type: 'numeric(20,8)' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
    { name: 'transactions', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'account_out', type: 'uuid', fk: 'accounts.id' },
      { name: 'account_in', type: 'uuid', fk: 'accounts.id' },
      { name: 'amount', type: 'numeric(20,8)' },
      { name: 'currency', type: 'char(3)' },
      { name: 'fee', type: 'numeric(20,8)' },
      { name: 'rate', type: 'numeric(20,8)' },
      { name: 'status', type: 'varchar(20)' },
      { name: 'type', type: 'varchar(20)' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
    { name: 'holds', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'account_id', type: 'uuid', fk: 'accounts.id' },
      { name: 'amount', type: 'numeric(20,8)' },
      { name: 'currency', type: 'char(3)' },
      { name: 'reason', type: 'varchar(100)' },
      { name: 'expires_at', type: 'timestamptz' },
    ]},
  ], 400, 1500),

  mkDb('db-orders-pg', 'svc-006', 'PostgreSQL', 'orders_db', [
    { name: 'orders', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid' },
      { name: 'pair_id', type: 'varchar(20)' },
      { name: 'type', type: 'varchar(10)', notes: 'limit/market' },
      { name: 'side', type: 'varchar(4)', notes: 'buy/sell' },
      { name: 'amount', type: 'numeric(20,8)' },
      { name: 'price', type: 'numeric(20,8)' },
      { name: 'status', type: 'varchar(20)' },
      { name: 'filled_amount', type: 'numeric(20,8)' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
    { name: 'trades', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'buy_order_id', type: 'uuid', fk: 'orders.id' },
      { name: 'sell_order_id', type: 'uuid', fk: 'orders.id' },
      { name: 'amount', type: 'numeric(20,8)' },
      { name: 'price', type: 'numeric(20,8)' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
  ], 700, 1500),

  mkDb('db-orderbook-redis', 'svc-007', 'Redis', 'orderbook_redis', [
    { name: 'bids:{pair}', columns: [
      { name: 'sorted_set', type: 'ZSET', notes: 'score=price, member=order_id' },
      { name: 'order:{id}', type: 'HASH', notes: 'order details' },
    ]},
    { name: 'asks:{pair}', columns: [
      { name: 'sorted_set', type: 'ZSET', notes: 'score=price, member=order_id' },
    ]},
  ], 960, 1500),

  mkDb('db-qe-pg', 'svc-010', 'PostgreSQL', 'quick_exchange_db', [
    { name: 'exchange_quotes', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid' },
      { name: 'from_currency', type: 'char(3)' },
      { name: 'to_currency', type: 'char(3)' },
      { name: 'from_amount', type: 'numeric(20,8)' },
      { name: 'to_amount', type: 'numeric(20,8)' },
      { name: 'rate', type: 'numeric(20,8)' },
      { name: 'expires_at', type: 'timestamptz' },
    ]},
    { name: 'exchange_transactions', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'quote_id', type: 'uuid', fk: 'exchange_quotes.id' },
      { name: 'user_id', type: 'uuid' },
      { name: 'status', type: 'varchar(20)' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
  ], 1200, 1500),

  mkDb('db-courses-tsdb', 'svc-012', 'TimescaleDB', 'courses_db', [
    { name: 'exchange_rates', columns: [
      { name: 'time', type: 'timestamptz', pk: true, notes: 'hypertable partition key' },
      { name: 'pair_id', type: 'varchar(20)' },
      { name: 'rate', type: 'numeric(20,8)' },
      { name: 'open', type: 'numeric(20,8)' },
      { name: 'high', type: 'numeric(20,8)' },
      { name: 'low', type: 'numeric(20,8)' },
      { name: 'close', type: 'numeric(20,8)' },
      { name: 'volume', type: 'numeric(20,8)' },
    ]},
    { name: 'providers', columns: [
      { name: 'id', type: 'int', pk: true },
      { name: 'name', type: 'varchar(100)' },
      { name: 'service', type: 'varchar(50)' },
      { name: 'active', type: 'boolean' },
    ]},
    { name: 'pairs', columns: [
      { name: 'id', type: 'int', pk: true },
      { name: 'provider_id', type: 'int', fk: 'providers.id' },
      { name: 'symbol', type: 'varchar(20)' },
      { name: 'active', type: 'boolean' },
    ]},
  ], 1450, 1500),

  mkDb('db-acqgw-pg', 'svc-014', 'PostgreSQL', 'acquiring_db', [
    { name: 'acquiring_transactions', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid' },
      { name: 'type', type: 'varchar(20)', notes: 'deposit/withdraw' },
      { name: 'status', type: 'varchar(20)' },
      { name: 'amount', type: 'numeric(20,8)' },
      { name: 'currency', type: 'char(3)' },
      { name: 'provider', type: 'varchar(50)' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
    { name: 'deposits', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'transaction_id', type: 'uuid', fk: 'acquiring_transactions.id' },
      { name: 'card_pan_last4', type: 'char(4)' },
      { name: 'bank_code', type: 'varchar(20)' },
      { name: 'three_ds_required', type: 'boolean' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
  ], 100, 1750),

  mkDb('db-kyc-pg', 'svc-021k', 'PostgreSQL', 'kyc_db', [
    { name: 'kyc_checks', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid' },
      { name: 'status', type: 'varchar(20)' },
      { name: 'score', type: 'int' },
      { name: 'started_at', type: 'timestamptz' },
      { name: 'finished_at', type: 'timestamptz', nullable: true },
    ]},
    { name: 'kyc_documents', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'check_id', type: 'uuid', fk: 'kyc_checks.id' },
      { name: 'type', type: 'varchar(50)' },
      { name: 's3_key', type: 'varchar(512)' },
      { name: 'verified_at', type: 'timestamptz', nullable: true },
    ]},
    { name: 'scoring_results', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'check_id', type: 'uuid', fk: 'kyc_checks.id' },
      { name: 'provider', type: 'varchar(50)' },
      { name: 'result', type: 'jsonb' },
      { name: 'created_at', type: 'timestamptz' },
    ]},
  ], 400, 1750),

  mkDb('db-notifications-pg', 'svc-023', 'PostgreSQL', 'notifications_db', [
    { name: 'notification_templates', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'name', type: 'varchar(100)' },
      { name: 'type', type: 'varchar(20)', notes: 'email/sms/push' },
      { name: 'subject', type: 'varchar(255)', nullable: true },
      { name: 'body', type: 'text' },
      { name: 'lang', type: 'char(2)' },
      { name: 'active', type: 'boolean' },
    ]},
    { name: 'notification_logs', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid' },
      { name: 'template_id', type: 'uuid', fk: 'notification_templates.id' },
      { name: 'channel', type: 'varchar(20)' },
      { name: 'status', type: 'varchar(20)' },
      { name: 'sent_at', type: 'timestamptz', nullable: true },
    ]},
  ], 700, 1750),

  mkDb('db-limiter-pg', 'svc-020', 'PostgreSQL', 'limiter_db', [
    { name: 'limits', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'code', type: 'varchar(50)' },
      { name: 'object_type', type: 'int', notes: '0=User, 1=Account, 2=Card' },
      { name: 'action_type', type: 'int', notes: '0-5: deposit/withdraw/...' },
      { name: 'limit_type', type: 'int', notes: '0=single, 1=daily, ...' },
      { name: 'value', type: 'numeric(20,8)' },
      { name: 'duration', type: 'int', notes: 'seconds' },
      { name: 'started_at', type: 'timestamptz' },
    ]},
    { name: 'limit_counters', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'limit_id', type: 'uuid', fk: 'limits.id' },
      { name: 'object_id', type: 'uuid' },
      { name: 'counter', type: 'numeric(20,8)' },
      { name: 'window_start', type: 'timestamptz' },
    ]},
  ], 960, 1750),

  mkDb('db-crypto-pg', 'svc-019', 'PostgreSQL', 'crypto_db', [
    { name: 'crypto_deposits', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid' },
      { name: 'currency', type: 'varchar(10)' },
      { name: 'network', type: 'varchar(20)' },
      { name: 'address', type: 'varchar(64)' },
      { name: 'amount', type: 'numeric(20,8)' },
      { name: 'tx_hash', type: 'varchar(128)' },
      { name: 'confirmations', type: 'int' },
      { name: 'status', type: 'varchar(20)' },
    ]},
    { name: 'crypto_withdrawals', columns: [
      { name: 'id', type: 'uuid', pk: true },
      { name: 'user_id', type: 'uuid' },
      { name: 'currency', type: 'varchar(10)' },
      { name: 'to_address', type: 'varchar(64)' },
      { name: 'amount', type: 'numeric(20,8)' },
      { name: 'tx_hash', type: 'varchar(128)', nullable: true },
      { name: 'status', type: 'varchar(20)' },
    ]},
  ], 1200, 1750),
];

// Zone background for DB layer
export const DB_ZONE_NODE: Node = {
  id: 'zone-db',
  type: 'zone',
  position: { x: 0, y: 1440 },
  style: { width: 2200, height: 500, zIndex: -10 },
  data: {
    label: 'Слой Баз Данных',
    zoneId: 'zone-db' as any,
    color: 'rgba(27,94,32,0.05)',
    borderColor: '#2E7D32',
  },
  draggable: false,
  selectable: false,
};

// Edges connecting services to their databases
export const DB_EDGES: Edge[] = [
  { id: 'e-db-users', source: 'svc-003', target: 'db-users-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-accounts', source: 'svc-004', target: 'db-accounts-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-orders', source: 'svc-006', target: 'db-orders-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-orderbook', source: 'svc-007', target: 'db-orderbook-redis', type: 'db-link', style: { stroke: '#D32F2F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#D32F2F' }, data: { label: 'Redis', isAsync: false, type: 'Redis', flowIds: [] } },
  { id: 'e-db-qe', source: 'svc-010', target: 'db-qe-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-courses', source: 'svc-012', target: 'db-courses-tsdb', type: 'db-link', style: { stroke: '#00796B', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#00796B' }, data: { label: 'TimescaleDB', isAsync: false, type: 'TimescaleDB', flowIds: [] } },
  { id: 'e-db-acqgw', source: 'svc-014', target: 'db-acqgw-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-kyc', source: 'svc-021k', target: 'db-kyc-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-notifications', source: 'svc-023', target: 'db-notifications-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-limiter', source: 'svc-020', target: 'db-limiter-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
  { id: 'e-db-crypto', source: 'svc-019', target: 'db-crypto-pg', type: 'db-link', style: { stroke: '#303F9F', strokeWidth: 1.5, strokeDasharray: '5 3' }, markerEnd: { type: MarkerType.Arrow, color: '#303F9F' }, data: { label: 'PostgreSQL', isAsync: false, type: 'PostgreSQL', flowIds: [] } },
];
